import type { RentalMoveOutResponse, RentalMoveOutReconciliation } from '@homevest/types/rentals'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  VStack,
  useToast
} from '@chakra-ui/react'
import { capabilities, rentals as RENTALS } from '@homevest/utils'

import axios from 'lib/axios'
import { parseDate, formatDate } from 'lib/date-time'
import { hasCapability } from 'lib/admin-perms'
import { getStatusForLeaseEndDateAndLeaseEndReason } from 'lib/rentals'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { MoveOutInfoFragment, WithRentalAgreementInfoFragment } from 'graphql/generated'
import { SmallLoading } from 'ui/Loading'
import startCase from 'lodash/startCase'
import { StoreState } from 'store'
import AcceleratedRentModal from './AcceleratedRentModal'
const { LEASE_END_REASONS } = RENTALS
const { LEDGER_SUPER_EDITOR } = capabilities.CAPABILITY_TYPES

interface MoveOutProps {
  onLedgerRefresh?: () => void
  onSuccess?: () => void
  rental: MoveOutInfoFragment & WithRentalAgreementInfoFragment
}

const cancelledReasons = [
  LEASE_END_REASONS.EVICTED,
  LEASE_END_REASONS.VACATED_IN_EVICTIONS,
  LEASE_END_REASONS.ABANDONED_LEASE,
  LEASE_END_REASONS.EARLY_TERMINATION_CASUALTY_LOSS,
  LEASE_END_REASONS.EARLY_TERMINATION_MILITARY,
  LEASE_END_REASONS.EARLY_TERMINATION_MAINTAINANCE_ISSUES,
  LEASE_END_REASONS.EARLY_TERMINATION_CUSTOMER_SERVICE_ISSUES,
  LEASE_END_REASONS.EARLY_TERMINATION_OTHER,
  LEASE_END_REASONS.NEVER_MOVED_IN,
  LEASE_END_REASONS.PURCHASED_HOME_DISPO
]

const completedReasons = [
  LEASE_END_REASONS.TRANSFERRED_HOMES,
  LEASE_END_REASONS.PURCHASED_OTHER_HOME,
  LEASE_END_REASONS.PRICE_TOO_HIGH,
  LEASE_END_REASONS.CHANGE_OF_ROOMMATES,
  LEASE_END_REASONS.MOVING_OUT_OF_AREA,
  LEASE_END_REASONS.MAINTAINANCE_ISSUES,
  LEASE_END_REASONS.CUSTOMER_SERVICE_ISSUES,
  LEASE_END_REASONS.NEED_LARGER_HOME,
  LEASE_END_REASONS.NEED_SMALLER_HOME,
  LEASE_END_REASONS.DONT_LIKE_NEIGHBORHOOD,
  LEASE_END_REASONS.PROBLEMS_WITH_NEIGHBORS,
  LEASE_END_REASONS.DO_NOT_RENEW
]

const MoveOut: React.FC<React.PropsWithChildren<MoveOutProps>> = ({
  onLedgerRefresh,
  onSuccess,
  rental
}) => {
  const { lease_end_reason, tenant_notice_given_on_date, move_out_date, final_liability_date } =
    rental

  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [leaseEndReason, setLeaseEndReason] = useState<string | null>()
  const [tenantNoticeDate, setTenantNoticeDate] = useState<Date | null>(null)
  const [moveOutDate, setMoveOutDate] = useState<Date | null>(null)
  const [finalLiabilityDate, setFinalLiabilityDate] = useState<Date | null>(null)
  const [isAcceleratedRentModalOpen, setIsAcceleratedRentModalOpen] = useState(false)
  const admin = useSelector((state: StoreState) => state.admin)
  const toast = useToast()

  useEffect(() => {
    setLeaseEndReason(lease_end_reason)
    setTenantNoticeDate(tenant_notice_given_on_date ? parseDate(tenant_notice_given_on_date) : null)
    setMoveOutDate(move_out_date ? parseDate(move_out_date) : null)
    setFinalLiabilityDate(final_liability_date ? parseDate(final_liability_date) : null)
  }, [lease_end_reason, tenant_notice_given_on_date, move_out_date, final_liability_date])

  const submitForm = async () => {
    if (!leaseEndReason || !tenantNoticeDate || !moveOutDate || !finalLiabilityDate) {
      alert('Please fill out all fields')
      return
    }

    const status = getStatusForLeaseEndDateAndLeaseEndReason(
      formatDate(moveOutDate),
      leaseEndReason,
      rental.status
    )

    try {
      setIsSaving(true)
      const savingBody = {
        status,
        lease_end_reason: leaseEndReason,
        tenant_notice_given_on_date: tenantNoticeDate,
        move_out_date: moveOutDate,
        final_liability_date: finalLiabilityDate
      }
      let reconciliation: RentalMoveOutReconciliation | undefined
      if (isScheduling) {
        // create a new move out
        reconciliation = (
          await axios.post<RentalMoveOutResponse>(`admin/rentals/${rental.id}/move_out`, savingBody)
        ).data.turn_project_reconciliation
      } else {
        // update move out
        reconciliation = (
          await axios.put<RentalMoveOutResponse>(`admin/rentals/${rental.id}/move_out`, savingBody)
        ).data.turn_project_reconciliation
      }

      toast({
        title: 'Successfully updated move out',
        status: 'success',
        duration: 3000,
        position: 'bottom-left'
      })
      showProjectSyncNotice(reconciliation)
      setIsEditing(false)
      onSuccess?.()
      onLedgerRefresh?.()
    } catch (error: any) {
      console.log(error)
      toast({
        title: 'Error updating move out',
        description: error.message,
        status: 'error',
        duration: 30000,
        position: 'bottom-left'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const showProjectSyncNotice = (result?: RentalMoveOutReconciliation) => {
    if (result?.outcome === 'needs_review') {
      toast({
        title: 'Move-out saved; turn project needs review',
        description:
          'The turn project could not be synchronized automatically. Review this home in Turn OS.',
        status: 'warning',
        duration: 10000,
        position: 'bottom-left'
      })
    } else if (result && result.mode !== 'apply') {
      toast({
        title: 'Automatic turn project updates are not active',
        description:
          'The move-out was saved. Turn project changes are disabled or in preview mode.',
        status: 'warning',
        duration: 10000,
        position: 'bottom-left'
      })
    }
  }

  const handleCancelMoveOut = async () => {
    try {
      setIsSaving(true)
      const { data } = await axios.delete<RentalMoveOutResponse>(
        `/admin/rentals/${rental.id}/move_out`,
        {
          data: { status: rental.status }
        }
      )
      showProjectSyncNotice(data.turn_project_reconciliation)
      setMoveOutDate(null)
      setTenantNoticeDate(null)
      setFinalLiabilityDate(null)
      setLeaseEndReason(null)
      toast({
        title: 'Successfully canceled move out',
        status: 'success',
        duration: 3000,
        position: 'bottom-left'
      })
      onSuccess?.()
      onLedgerRefresh?.()
    } catch (error: any) {
      console.log(error)
      toast({
        title: 'Error canceling move out',
        description: error.message,
        status: 'error',
        duration: 30000,
        position: 'bottom-left'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const canManageAcceleratedRent = hasCapability(admin, LEDGER_SUPER_EDITOR)
  const liabilityDate = finalLiabilityDate && formatDate(finalLiabilityDate)
  const moveOutDateString = moveOutDate && formatDate(moveOutDate)
  const warningLeaseEndDate = moveOutDateString
    ? rental.rental_agreement_histories.find(({ ends_at }) => ends_at > moveOutDateString)?.ends_at
    : null
  const showLiabilityWarning =
    leaseEndReason !== LEASE_END_REASONS.NEVER_MOVED_IN &&
    moveOutDateString &&
    liabilityDate &&
    liabilityDate <= moveOutDateString &&
    warningLeaseEndDate

  const AcceleratedRentButton = canManageAcceleratedRent ? (
    <Button
      size='xs'
      colorScheme='teal'
      variant='outline'
      isDisabled={isSaving || isEditing}
      onClick={(e) => {
        e.stopPropagation()
        setIsAcceleratedRentModalOpen(true)
      }}
    >
      Manage Accelerated Rent
    </Button>
  ) : null

  const ActionButton = (isSaving: boolean) => {
    if (isEditing) {
      return (
        <HStack>
          <Button
            size='xs'
            isDisabled={isSaving}
            isLoading={isSaving}
            onClick={(e) => {
              e.stopPropagation()
              setLeaseEndReason(lease_end_reason)
              setTenantNoticeDate(
                tenant_notice_given_on_date ? parseDate(tenant_notice_given_on_date) : null
              )
              setMoveOutDate(move_out_date ? parseDate(move_out_date) : null)
              setFinalLiabilityDate(final_liability_date ? parseDate(final_liability_date) : null)
              setIsEditing(false)
            }}
          >
            Cancel
          </Button>
          <Button
            size='xs'
            isLoading={isSaving}
            onClick={(e) => {
              e.stopPropagation()
              submitForm()
            }}
            isDisabled={
              isSaving ||
              !leaseEndReason ||
              !tenantNoticeDate ||
              !moveOutDate ||
              !finalLiabilityDate
            }
          >
            Save
          </Button>
        </HStack>
      )
    }

    return (
      <HStack>
        {moveOutDate ? (
          <>
            <Button
              size='xs'
              isDisabled={isSaving}
              isLoading={isSaving}
              onClick={(e) => {
                e.stopPropagation()
                handleCancelMoveOut()
              }}
            >
              Cancel Move Out
            </Button>
            <Button
              size='xs'
              colorScheme='teal'
              isDisabled={isSaving}
              isLoading={isSaving}
              onClick={(e) => {
                e.stopPropagation()
                setIsEditing(true)
                setIsScheduling(false)
              }}
            >
              Update Move Out
            </Button>
          </>
        ) : (
          <Button
            size='xs'
            colorScheme='teal'
            isDisabled={isSaving}
            isLoading={isSaving}
            onClick={(e) => {
              e.stopPropagation()
              setIsEditing(true)
              setIsScheduling(true)
            }}
          >
            Schedule Move Out
          </Button>
        )}
      </HStack>
    )
  }

  const hasMoveOutBeenScheduled = !!moveOutDate
  const showForm = isEditing || hasMoveOutBeenScheduled
  return (
    <>
      <ContentSectionCard
        title='Move Out'
        action={
          <HStack>
            {AcceleratedRentButton}
            {ActionButton(isSaving)}
          </HStack>
        }
      >
        {isSaving ? (
          <SmallLoading />
        ) : showForm ? (
          <VStack width='100%' py='4' px='8' align='stretch'>
            <FormControl width='50%' isDisabled={!isEditing} isRequired>
              <FormLabel>Lease End Reason</FormLabel>
              <Select
                _disabled={{ opacity: 1 }}
                onChange={(e) => {
                  setLeaseEndReason(e.target.value)
                }}
                placeholder='-'
                value={leaseEndReason || ''}
              >
                <optgroup label='Cancelled'>
                  {cancelledReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {startCase(reason)}
                    </option>
                  ))}
                </optgroup>
                <optgroup label='Completed'>
                  {completedReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {startCase(reason)}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </FormControl>
            <FormControl width='50%' isDisabled={!isEditing} isRequired>
              <FormLabel>Notice Given On</FormLabel>
              <Input
                _disabled={{ opacity: 1 }}
                type='date'
                max={new Date().toISOString().split('T')[0]}
                value={tenantNoticeDate?.toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = parseDate(e.target.value)
                  setTenantNoticeDate(date)
                  if (leaseEndReason === LEASE_END_REASONS.NEVER_MOVED_IN) {
                    setMoveOutDate(date)
                    setFinalLiabilityDate(date)
                  }
                }}
              />
            </FormControl>
            <FormControl
              width='50%'
              isDisabled={!isEditing || leaseEndReason === LEASE_END_REASONS.NEVER_MOVED_IN}
              isRequired
            >
              <FormLabel>Move Out Date</FormLabel>
              <Input
                _disabled={{ opacity: 1 }}
                type='date'
                value={moveOutDate?.toISOString().split('T')[0]}
                onChange={(e) => setMoveOutDate(parseDate(e.target.value))}
              />
            </FormControl>
            <FormControl
              width='50%'
              isDisabled={!isEditing || leaseEndReason === LEASE_END_REASONS.NEVER_MOVED_IN}
              isRequired
            >
              <FormLabel>Final Liability Date</FormLabel>
              <Input
                _disabled={{ opacity: 1 }}
                type='date'
                value={finalLiabilityDate?.toISOString().split('T')[0]}
                onChange={(e) => setFinalLiabilityDate(parseDate(e.target.value))}
              />
            </FormControl>
            {showLiabilityWarning && (
              <Alert status='warning'>
                <AlertIcon />
                <AlertDescription>
                  Lease ends {formatDate(parseDate(warningLeaseEndDate), 'MM/DD/YYYY')}, so
                  accelerated rent is expected. Choosing this final liability date would add $0 of
                  accelerated rent.
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        ) : (
          <div />
        )}
      </ContentSectionCard>
      <AcceleratedRentModal
        isOpen={isAcceleratedRentModalOpen}
        onClose={() => setIsAcceleratedRentModalOpen(false)}
        onSuccess={onLedgerRefresh}
        rentalId={rental.id}
      />
    </>
  )
}

export default MoveOut
